import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-arrow-button-right-2';
import '@fil-global/fil-button';

import componentStyles from './fil-text-media-block.styles.js';

/**
 * @tag fil-text-media-block
 * @property {'primary'|'secondary'} [appearance] Appearance of the component
 * @property {object} [ctaExtraProps] Extra properties of the CTA in the component
 * @property {string} [ctaIcon] Icon to be used in the CTA of the component
 * @property {string} [ctaIconSize] Icon size of the CTA of the component
 * @property {'primary'|'secondary'|'tertiary'|'textlink'} [ctaKind] Kind of the CTA in the component
 * @property {string} [ctaText] Label text of CTA in the component
 * @property {string} [ctaUrl] URL of CTA in the  component
 * @property {string} [header] Header of the component
 * @property {string} [imageUrl] Image URL of the component
 * @property {'left'|'right'} [mediaAlign] Alignment of the media in the component
 * @property {boolean} [toggleContainer] Toggle container property of the component
 * @property {string} [videoImageUrl] Video image URL of the component
 * @property {string} [videoUrl] Video URL of the component
 */
export class FilTextMediaBlock extends LitElement {
  static properties = {
    appearance: { type: String }, // 'primary' | 'secondary'
    ctaExtraProps: { type: Object },
    ctaIcon: { type: String },
    ctaIconSize: { type: Number },
    ctaKind: { type: String }, // "primary" | "secondary" | "tertiary" | "textlink"
    ctaText: { type: String },
    ctaUrl: { type: String },
    header: { type: String },
    imageUrl: { type: String },
    mediaAlign: { type: String }, // "left" | "right"
    toggleContainer: { type: Boolean },
    videoImageUrl: { type: String },
    videoUrl: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.appearance = 'primary';
    this.mediaAlign = 'right';
    this.toggleContainer = false;
  }

  ctaTemplate() {
    if (this.ctaKind) {
      return html`
        <p
          class="fil-text-media-block__cta ${this.ctaKind === 'textlink'
            ? 'fil-text-media-block__cta--link'
            : 'fil-text-media-block__cta'}"
        >
          <fil-button
            kind="${this.ctaKind}"
            label="${this.ctaText}"
            url="${ifDefined(this.ctaUrl)}"
            size="standard"
            icon="${this.ctaIcon}"
            iconPosition="right"
            iconSize="${this.ctaIconSize}"
            extraProps=${ifDefined(this.ctaExtraProps)
              ? JSON.stringify({ ...this.ctaExtraProps })
              : null}
          ></fil-button>
        </p>
      `;
    }

    return html``;
  }

  mediaTemplate() {
    if (this.imageUrl) {
      return html`<img src="${this.imageUrl}" alt="text media grid image" />`;
    }

    if (this.videoUrl) {
      const styles = { backgroundImage: `url(${this.videoImageUrl})` };
      let isWebMedia = true;
      if (
        this.videoUrl.indexOf('video.fidelity.tv') >= 0 ||
        this.videoUrl.indexOf('www.youtube.com') >= 0
      ) {
        isWebMedia = false;
      }

      return html`<div class="fil-text-media-block__video-wrapper">
        ${isWebMedia
          ? html`<video controls src="${this.videoUrl}">
              Sorry, your browser does not support embedded videos, but do not worry, you can
              <a href="${this.videoUrl}">download it</a>
              and watch it with your favorite video player!
            </video>`
          : html`<iframe
              title="player"
              id="iframe-video"
              name="iframe-video"
              src="${this.videoUrl}"
              allow="fullscreen"
              allowfullscreen=""
              >Your browser must support iframes to play this content.</iframe
            >`}
        <div class="fil-text-media-block__video-image" style=${styleMap(styles)}></div>
        <div class="fil-text-media-block__icon-container">
          <fil-icon-arrow-button-right-2
            class="fil-text-media-block__icon"
            size="24"
            @click="${this.handlePlayer}"
          >
          </fil-icon-arrow-button-right-2>
        </div>
      </div>`;
    }

    return html``;
  }

  handlePlayer() {
    const video = this.shadowRoot.querySelector('video');
    if (video) {
      video.play();
    }

    const videoIframe = this.shadowRoot.querySelector('iframe');
    if (videoIframe) {
      const videoUrl = videoIframe.src;
      const playStr = 'true';
      const newVideoUrl = videoUrl.replace(/(autoPlay=).*?(&)/, `$1${playStr}$2`);
      videoIframe.src = newVideoUrl;
    }

    const videoImage = this.shadowRoot.querySelector('.fil-text-media-block__video-image');
    const videoPlayIcon = this.shadowRoot.querySelector('.fil-text-media-block__icon-container');
    videoImage.classList.add('is-hidden');
    videoPlayIcon.classList.add('is-hidden');
  }

  handlePlayerEnd() {
    const videoImage = this.shadowRoot.querySelector('.fil-text-media-block__video-image');
    videoImage.classList.remove('is-hidden');
  }

  render() {
    const classes = {
      [`fil-text-media-block--${this.appearance}`]: this.appearance !== 'primary',
      'fil-text-media-block--reverse': this.mediaAlign === 'left',
      'fil-text-media-block--toggled': this.mediaAlign === 'left' && this.toggleContainer,
    };

    return html`
      <section class="fil-text-media-block${classMap(classes)}">
        <div class="fil-text-media-block__section fil-text-media-block__section-media">
          ${this.mediaTemplate()}
        </div>
        <div class="fil-text-media-block__section fil-text-media-block__section-body">
          ${this.header
            ? html`<h2 class="fil-text-media-block__header">${this.header}</h2>`
            : nothing}
          <p class="fil-text-media-block__content">
            <slot></slot>
          </p>
          ${this.ctaTemplate()}
        </div>
      </section>
    `;
  }
}

export default FilTextMediaBlock;
